/**
 * Cart Selectors
 * 
 * Computed values and derived state from the cart.
 * These are pure functions that don't modify state.
 */

import { CartItem } from '@/lib/shopify';

/**
 * Calculate total number of items in cart
 * Sums up quantities of all items
 */
export const selectTotalItems = (items: CartItem[]): number => {
  return items.reduce((sum, item) => sum + item.quantity, 0);
};

/**
 * Calculate total price of all items in cart
 * Returns the raw number (not formatted)
 */
export const selectTotalPrice = (items: CartItem[]): number => {
  return items.reduce((sum, item) => {
    const itemPrice = parseFloat(item.price.amount);
    return sum + (itemPrice * item.quantity);
  }, 0);
};

/**
 * Get currency code from cart items
 * Uses first item's currency, assumes all items use same currency
 */
export const selectCurrency = (items: CartItem[]): string => {
  return items[0]?.price.currencyCode || 'USD';
};

/**
 * Format total price with currency
 */
export const selectFormattedTotal = (items: CartItem[]): string => {
  const total = selectTotalPrice(items);
  const currency = selectCurrency(items);
  return `${currency} $${total.toFixed(2)}`;
};

/**
 * Check if cart is empty
 */
export const selectIsEmpty = (items: CartItem[]): boolean => {
  return items.length === 0;
};

/**
 * Check if cart has specific item
 */
export const selectHasItem = (items: CartItem[], variantId: string): boolean => {
  return items.some(item => item.variantId === variantId);
};

/**
 * Get quantity of specific item
 */
export const selectItemQuantity = (items: CartItem[], variantId: string): number => {
  const item = items.find(i => i.variantId === variantId);
  return item?.quantity || 0;
};

/**
 * Find item by variant ID
 */
export const selectItemByVariantId = (
  items: CartItem[],
  variantId: string
): CartItem | undefined => {
  return items.find(item => item.variantId === variantId);
};

/**
 * Group items by product (for display purposes)
 */
export const selectItemsByProduct = (items: CartItem[]): Map<string, CartItem[]> => {
  const grouped = new Map<string, CartItem[]>();
  
  items.forEach(item => {
    const productId = item.product.node.id;
    const existing = grouped.get(productId) || [];
    grouped.set(productId, [...existing, item]);
  });
  
  return grouped;
};

/**
 * Get cart summary for logging/display
 */
export const selectCartSummary = (items: CartItem[]): string => {
  const totalItems = selectTotalItems(items);
  const totalPrice = selectTotalPrice(items);
  const currency = selectCurrency(items);
  
  return `${totalItems} item(s) - ${currency} $${totalPrice.toFixed(2)}`;
};

/**
 * Validate if cart is ready for checkout
 */
export const selectCanCheckout = (items: CartItem[], isLoading: boolean): boolean => {
  return items.length > 0 && !isLoading;
};
